import React, { useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaFacebook, FaInstagram, FaPhone, FaClock, FaRocket, FaCheckCircle, FaExclamationTriangle, FaChevronDown } from 'react-icons/fa';
import SEO from '../../components/common/SEO';
import { Reveal, Breadcrumb } from '../../components/common';
import { submitContactMessage } from '../../services/contactService';
import { notifySuccess, notifyError, notifyWarning, NotificationMessages } from '../../utils/notifications';

const FAQS = [
    { q: "How long does a project take?", a: "Most projects are completed within 2-4 weeks, depending on complexity. Rush projects can be accommodated with additional fees." },
    { q: "Do you offer revisions?", a: "Yes! We include 3 rounds of revisions with every project to ensure your complete satisfaction." },
    { q: "What's your pricing structure?", a: "Pricing varies by project scope and complexity. We offer competitive rates and custom packages for different budgets." },
    { q: "Do you work with startups?", a: "Absolutely! We love working with startups and offer special packages for new businesses looking to establish their brand." },
    { q: "Do you work remotely?", a: "Yes! We work with clients worldwide via video calls, email, and project management tools for seamless collaboration." },
    { q: "What's included in brand identity?", a: "Complete brand identity includes logo design, color palette, typography, business cards, letterhead, and brand guidelines." },
    { q: "Do you offer ongoing support?", a: "Yes! We provide post-project support and maintenance packages to help you maintain your brand consistency." },
    { q: "How do we get started?", a: "Simply fill out the contact form or email us with your project details. We'll respond within 24 hours with a custom proposal." },
    { q: "What makes you different?", a: "We combine strategic thinking with creative excellence, ensuring your brand not only looks great but also communicates effectively with your target audience." },
];

const inputClass = "w-full px-4 py-3 rounded-xl border border-designer-dark/15 bg-white text-designer-dark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-designer-primary/40 focus:border-designer-primary transition-all";
const labelClass = "block text-[13px] font-bold tracking-wide text-designer-dark mb-2";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) {
            setError(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.name.trim()) {
            notifyWarning(NotificationMessages.VALIDATION_NAME_REQUIRED, { duration: 3000 });
            return;
        }
        if (formData.name.trim().length < 2) {
            notifyWarning(NotificationMessages.VALIDATION_NAME_TOO_SHORT, { duration: 3000 });
            return;
        }
        if (!formData.email.trim()) {
            notifyWarning(NotificationMessages.VALIDATION_EMAIL_REQUIRED, { duration: 3000 });
            return;
        }
        if (!formData.subject) {
            notifyWarning(NotificationMessages.VALIDATION_SUBJECT_REQUIRED, { duration: 3000 });
            return;
        }
        if (!formData.message.trim()) {
            notifyWarning(NotificationMessages.VALIDATION_MESSAGE_REQUIRED, { duration: 3000 });
            return;
        }
        if (formData.message.trim().length < 10) {
            notifyWarning('Message must be at least 10 characters long', { duration: 3000 });
            return;
        }

        setIsSubmitting(true);

        try {
            await submitContactMessage({
                name: formData.name.trim(),
                email: formData.email.trim(),
                subject: formData.subject,
                message: formData.message.trim(),
            });

            setSubmitted(true);
            notifySuccess(NotificationMessages.CONTACT_SENT, { duration: 5000 });
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: ''
            });

            setTimeout(() => {
                setSubmitted(false);
            }, 5000);
        } catch (err) {
            console.error('Failed to send contact message:', err);
            const errorMsg = err.message || NotificationMessages.CONTACT_ERROR;
            setError(errorMsg);
            notifyError(errorMsg, { duration: 6000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Arvix Creative - Creative Studio",
        "description": "Get in touch with Arvix Creative for professional creative services. Contact us for brand identity, UI/UX design, web development, and visual solutions. Based in Chattogram, Bangladesh.",
        "url": "https://arvixcreative.com/contact",
        "mainEntity": {
            "@type": "Organization",
            "name": "Arvix Creative",
            "alternateName": ["Tanvirus Pixels", "Tanvir Creative Studio"],
            "url": "https://arvixcreative.com",
            "logo": "https://arvixcreative.com/logo512.png",
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+8801645076455",
                "contactType": "customer service",
                "email": "arvixcreative@gmail.com",
                "areaServed": "BD",
                "availableLanguage": ["English", "Bengali"]
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "01No. Goli, Sakib Tower, Keranihat, Satkania",
                "addressLocality": "Chattogram",
                "addressCountry": "BD"
            },
            "sameAs": [
                "https://www.facebook.com/arvix.creative",
                "https://www.instagram.com/arvix.creative"
            ]
        }
    };

    return (
        <>
            <SEO
                title="Contact Us - Arvix Creative | Get in Touch for Creative Services"
                description="Get in touch with Arvix Creative for professional creative services. Contact us for brand identity, UI/UX design, web development, and visual solutions. Email: arvixcreative@gmail.com | Phone: +880 1645 076455 (Call or WhatsApp) | Based in Chattogram, Bangladesh."
                keywords="contact arvix creative, creative studio contact, creative agency Bangladesh, Chattogram services, brand identity contact, UI/UX design contact, web development, tanvirus pixels contact"
                url="https://arvixcreative.com/contact"
                structuredData={structuredData}
            />
            <div className="min-h-screen pt-24 pb-20">
                <Breadcrumb items={[{ label: "Contact" }]} className="mb-8" />
                {/* Page hero */}
                <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-3">
                                <span className="tick" aria-hidden="true" />
                                <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-primary">
                                    Contact
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={90}>
                            <h1 className="text-balance mt-5 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-designer-dark">
                                Tell us about the project keeping you up at night
                            </h1>
                        </Reveal>
                    </div>
                    <Reveal delay={170}>
                        <p className="text-base md:text-lg text-gray-600 leading-relaxed lg:justify-self-end lg:max-w-md">
                            One message is all it takes. Share your goals and timeline, and we
                            will reply with honest advice and a custom proposal.
                        </p>
                    </Reveal>
                </div>

                {/* Form + info */}
                <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-5 md:gap-6 items-start">
                    {/* Contact form */}
                    <Reveal className="h-full">
                        <div className="h-full rounded-[2rem] border border-designer-dark/10 bg-white p-6 sm:p-8 md:p-10 shadow-[0_24px_60px_-30px_rgba(29,32,48,0.25)]">
                            {submitted ? (
                                <div className="text-center py-14">
                                    <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <FaCheckCircle className="w-10 h-10 text-white" />
                                    </div>
                                    <h2 className="font-display text-2xl md:text-3xl font-bold text-designer-dark mb-3">
                                        Message sent successfully!
                                    </h2>
                                    <p className="text-gray-600 mb-8 max-w-sm mx-auto">
                                        Thank you for reaching out. We will get back to you within 24 hours.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="px-7 py-3 bg-designer-primary text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-colors"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="mb-8">
                                        <h2 className="font-display text-2xl md:text-[1.7rem] font-bold text-designer-dark">
                                            Send us a message
                                        </h2>
                                        <p className="mt-2 text-gray-600">
                                            Tell us about your project and we will get back to you with a custom proposal.
                                        </p>
                                    </div>
                                    {error && (
                                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                                            <FaExclamationTriangle className="text-red-500 text-lg flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-red-800 text-sm font-bold mb-1">Error sending message</p>
                                                <p className="text-red-700 text-sm">{error}</p>
                                            </div>
                                        </div>
                                    )}
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label htmlFor="name" className={labelClass}>Your name</label>
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    className={inputClass}
                                                    placeholder="Tanvirul Islam"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="email" className={labelClass}>Your email</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    className={inputClass}
                                                    placeholder="imtanvirus@gmail.com"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="subject" className={labelClass}>Subject</label>
                                            <div className="relative">
                                                <select
                                                    id="subject"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                    className={`${inputClass} pr-10 appearance-none cursor-pointer`}
                                                >
                                                    <option value="">Select a query type</option>
                                                    <option value="Project Query">Project Query</option>
                                                    <option value="Course Query">Course Query</option>
                                                    <option value="Package Query">Package Query</option>
                                                    <option value="Service Query">Service Query</option>
                                                </select>
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                                    <FaChevronDown className="text-gray-400 text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="message" className={labelClass}>Your message</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                rows="5"
                                                className={`${inputClass} resize-none`}
                                                placeholder="Tell us about your project, goals, timeline, and any specific requirements..."
                                            ></textarea>
                                        </div>
                                        <div className="pt-2">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full px-8 py-4 bg-designer-primary text-white rounded-full font-semibold hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-70"
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></span>
                                                        Sending message...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaRocket className="mr-3" />
                                                        Send message
                                                    </>
                                                )}
                                            </button>
                                            <p className="mt-3 text-center text-[13px] text-gray-400">
                                                Average reply time: under 24 hours on working days.
                                            </p>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </Reveal>

                    {/* Info rail */}
                    <div className="space-y-5 md:space-y-6">
                        <Reveal delay={110}>
                            <div className="relative overflow-hidden rounded-[2rem] bg-designer-dark p-7 md:p-8">
                                <div className="absolute inset-0 texture-grid-dark opacity-60" aria-hidden="true" />
                                <div className="relative">
                                    <h2 className="font-display text-xl font-bold text-white">Get in touch</h2>
                                    <p className="mt-1.5 text-sm text-white/55">
                                        Prefer talking to typing? Reach us directly.
                                    </p>
                                    <ul className="mt-6 space-y-5">
                                        <li className="flex items-start gap-4">
                                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-designer-accent">
                                                <FaEnvelope className="h-4 w-4" />
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-white/40">Email</span>
                                                <a href="mailto:arvixcreative@gmail.com" className="text-[15px] font-semibold text-white hover:text-designer-accent transition-colors break-all">
                                                    arvixcreative@gmail.com
                                                </a>
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-4">
                                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-designer-accent">
                                                <FaPhone className="h-4 w-4" />
                                            </span>
                                            <span>
                                                <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-white/40">Phone</span>
                                                <a
                                                    href="https://wa.me/8801645076455"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[15px] font-semibold text-white hover:text-designer-accent transition-colors"
                                                    title="Chat with us on WhatsApp"
                                                >
                                                    +880 1645 076455 (Call or WhatsApp)
                                                </a>
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-4">
                                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-designer-accent">
                                                <FaMapMarkerAlt className="h-4 w-4" />
                                            </span>
                                            <span>
                                                <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-white/40">Studio</span>
                                                <a
                                                    href="https://maps.app.goo.gl/5jRZ3P2BadXRheqV9"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[15px] font-semibold text-white hover:text-designer-accent transition-colors"
                                                    title="View location on Google Maps"
                                                >
                                                    Sakib Tower, Keranihat, Satkania, Chattogram, Bangladesh
                                                </a>
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-4">
                                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-designer-accent">
                                                <FaClock className="h-4 w-4" />
                                            </span>
                                            <span>
                                                <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-white/40">Response time</span>
                                                <span className="text-[15px] font-semibold text-white">Within 24 hours</span>
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={190}>
                            <div className="rounded-[2rem] border border-designer-dark/10 bg-[#FBFAF9] p-7 md:p-8">
                                <h2 className="font-display text-xl font-bold text-designer-dark">Follow the work</h2>
                                <p className="mt-1.5 text-sm text-gray-500">
                                    Fresh drops and behind the scenes, straight from the studio floor.
                                </p>
                                <div className="mt-5 flex gap-3">
                                    <a
                                        href="https://www.facebook.com/arvix.creative"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Arvix Creative on Facebook"
                                        className="flex h-12 w-12 items-center justify-center rounded-full border border-designer-dark/15 text-designer-dark transition-all hover:border-designer-primary hover:bg-designer-primary hover:text-white"
                                    >
                                        <FaFacebook size={19} />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/arvix.creative"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Arvix Creative on Instagram"
                                        className="flex h-12 w-12 items-center justify-center rounded-full border border-designer-dark/15 text-designer-dark transition-all hover:border-designer-primary hover:bg-designer-primary hover:text-white"
                                    >
                                        <FaInstagram size={19} />
                                    </a>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>

                {/* FAQ */}
                <div className="mt-16 md:mt-24">
                    <div className="flex items-center gap-3">
                        <span className="tick" aria-hidden="true" />
                        <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-primary">
                            Quick FAQ
                        </p>
                    </div>
                    <Reveal delay={80}>
                        <h2 className="text-balance mt-4 max-w-2xl font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-designer-dark leading-tight">
                            Asked often, answered honestly
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {FAQS.map((faq, i) => (
                            <Reveal key={faq.q} delay={(i % 3) * 90} className="h-full">
                                <div className="h-full rounded-3xl border border-designer-dark/10 bg-white p-6 transition-shadow duration-300 hover:shadow-[0_24px_60px_-28px_rgba(29,32,48,0.35)]">
                                    <p className="font-mono text-[11px] tracking-[0.22em] text-designer-primary">
                                        Q{String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-2 font-display text-lg font-bold text-designer-dark leading-snug">
                                        {faq.q}
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;
